/**
 * A list of email addresses, entered as chips.
 *
 * Built for the realistic input rather than the tidy one: an operator setting up
 * a school has the addresses in an email or a spreadsheet cell, so a paste of
 * "head@x.edu, deputy@x.edu" or a column of newline-separated addresses splits
 * into chips instead of landing as one malformed entry. Enter, comma, space and
 * blur all commit — whichever the person happens to reach for.
 *
 * Validation is deliberately shallow. The server validates properly; this only
 * catches the typo worth catching at the keyboard (no `@`, no dot after it) so
 * a mistyped address is visible before submit rather than after a 422.
 */

import { useState, type KeyboardEvent, type ClipboardEvent } from 'react'
import { X } from 'lucide-react'

/** Loose on purpose — see the note above. */
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function splitEmails(raw: string): string[] {
  return raw
    .split(/[\s,;]+/)
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean)
}

export function EmailList({
  emails,
  onChange,
  placeholder,
  inputId,
}: {
  emails: string[]
  onChange: (emails: string[]) => void
  placeholder?: string
  inputId?: string
}) {
  const [draft, setDraft] = useState('')

  function commit(raw: string) {
    const next = [...emails]
    for (const email of splitEmails(raw)) {
      if (!next.includes(email)) next.push(email)
    }
    onChange(next)
    setDraft('')
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' || event.key === ',' || event.key === ' ') {
      if (draft.trim()) {
        event.preventDefault()
        commit(draft)
      }
      return
    }
    // Backspace on an empty field removes the last chip, which is what every
    // other chip input does and what the hand expects.
    if (event.key === 'Backspace' && !draft && emails.length) {
      onChange(emails.slice(0, -1))
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>) {
    const text = event.clipboardData.getData('text')
    if (!/[\s,;]/.test(text)) return // a single address: let it type normally
    event.preventDefault()
    commit(text)
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-2xl bg-raised px-2.5 py-2">
      {emails.map((email) => {
        const invalid = !LOOKS_LIKE_EMAIL.test(email)
        return (
          <span
            key={email}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
              invalid ? 'bg-danger-wash text-danger' : 'bg-accent-wash text-accent'
            }`}
          >
            {email}
            <button
              type="button"
              onClick={() => onChange(emails.filter((e) => e !== email))}
              aria-label={`Remove ${email}`}
              className="opacity-60 transition hover:opacity-100"
            >
              <X size={12} />
            </button>
          </span>
        )
      })}
      <input
        id={inputId}
        type="email"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={() => draft.trim() && commit(draft)}
        placeholder={emails.length ? '' : placeholder}
        className="min-w-[10rem] flex-1 bg-transparent px-1 py-1 text-sm text-ink outline-none placeholder:text-faint"
      />
    </div>
  )
}
